import { useEffect, useRef } from "react";

/**
 * Cloudflare Turnstile widget. Loads the upstream script once, renders the
 * widget into a div, and calls `onToken` with the resulting token (or `null`
 * when the widget expires / errors).
 *
 * Uses the "managed" mode - Cloudflare decides whether the user gets an
 * invisible pass or a visible interaction. Themed to inherit the page's
 * dark/light state.
 */
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

function loadTurnstileScript() {
  return new Promise((resolve, reject) => {
    if (window.turnstile) { resolve(); return; }
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", reject);
      return;
    }
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

export default function TurnstileWidget({ onToken, theme = "auto" }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const sitekey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  useEffect(() => {
    if (!sitekey || !containerRef.current) return undefined;
    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !window.turnstile || !containerRef.current) return;
        // Belt-and-braces: if the script previously rendered into this node, clear it first.
        try { while (containerRef.current.firstChild) containerRef.current.removeChild(containerRef.current.firstChild); } catch {}
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey,
          theme,
          retry: "auto",
          callback: (token) => onToken?.(token),
          "error-callback": () => onToken?.(null),
          "expired-callback": () => onToken?.(null),
          "timeout-callback": () => onToken?.(null),
        });
      })
      .catch(() => onToken?.(null));

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch {}
        widgetIdRef.current = null;
      }
    };
  }, [sitekey, theme, onToken]);

  if (!sitekey) return null;

  return (
    <div className="mt-4" data-testid="turnstile-widget">
      <div ref={containerRef} />
    </div>
  );
}
